import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCreditCard, FaMapMarkerAlt, FaCartPlus, FaBoxOpen, FaTags,
  FaWallet, FaTruck, FaTimes, FaMoneyBillWave, FaEdit, FaSave,
  FaGooglePay, FaCcVisa, FaCheckCircle
} from "react-icons/fa";
import Navbar from "./Navbar";
import Fotter from "./Fotter";
import axios from "axios";
import { BASE_URL } from "../backend/backend.js";

const formatPrice = (p) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(p);

const OrderSummary = () => {
  const [cartItems, setCartItems] = useState([]);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [address, setAddress] = useState("");
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [tempAddress, setTempAddress] = useState("");

  useEffect(() => {
    axios.get(`${BASE_URL}/user/getAllCartProduct`, { withCredentials: true })
      .then(res => res.data?.body && setCartItems(res.data.body))
      .catch(err => console.error("Cart fetch error:", err));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const res = await axios.post(`${BASE_URL}/api/saveLocation`, 
            { latitude: pos.coords.latitude, longitude: pos.coords.longitude }, 
            { withCredentials: true });
          if (typeof res.data === "string") { setAddress(res.data); setTempAddress(res.data); }
        } catch (e) { console.error("Location error", e); }
      });
    }
  }, []);

  const originalTotal = cartItems.reduce((acc, i) => acc + i.medicine.price * i.quantity, 0);
  const discountedTotal = cartItems.reduce((acc, i) => acc + i.medicine.descountPrice * i.quantity, 0);
  const otherCharges = 50;
  const finalTotal = discountedTotal + otherCharges;
  const totalSavings = originalTotal - discountedTotal;

  const handleSubmitPayment = () => {
    const requestBody = {
      paymentMode: paymentMethod,
      subTotal: discountedTotal,
      discount: totalSavings,
      otherCharge: otherCharges,
      totalPrice: finalTotal,
      fullAddress: address,
    };

    axios.post(`${BASE_URL}/user/placeOrder`, requestBody, { withCredentials: true })
      .then(() => {
        alert("Order Placed Successfully!");
        setIsCheckoutModalOpen(false);
      })
      .catch(() => alert("Payment failed. Please try again."));
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Navbar />
      
      <main style={mainContentStyle}>
        <div style={contentGrid}>
          
          {/* Left Column: Items and Address */}
          <div style={leftColumn}>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} style={sectionCard}>
              <h2 style={sectionTitle}><FaBoxOpen color="#3b82f6" /> Review Your Items</h2>
              {cartItems.map((item, idx) => (
                <div key={idx} style={itemRow}>
                  <div style={itemInfo}>
                    <div style={itemBadge}>{item.quantity}x</div>
                    <div>
                      <h4 style={itemName}>{item.medicine.medicineName}</h4>
                      <p style={itemSubtext}>{item.medicine.manufacturerName || "Girraj Medicare"}</p>
                    </div>
                  </div>
                  <div style={itemPriceBlock}>
                    <span style={oldPriceText}>{formatPrice(item.medicine.price * item.quantity)}</span>
                    <span style={newPriceText}>{formatPrice(item.medicine.descountPrice * item.quantity)}</span>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} style={sectionCard}>
              <div style={flexHeader}>
                <h2 style={sectionTitle}><FaMapMarkerAlt color="#ef4444" /> Delivery Address</h2>
                <button onClick={() => isEditingAddress ? setAddress(tempAddress) || setIsEditingAddress(false) : setIsEditingAddress(true)} style={editBtn}>
                  {isEditingAddress ? <><FaSave /> Save</> : <><FaEdit /> Change</>}
                </button>
              </div>
              {isEditingAddress ? (
                <textarea value={tempAddress} onChange={(e) => setTempAddress(e.target.value)} style={addressInput} rows={3} />
              ) : (
                <p style={addressDisplay}>{address || "Detecting your location..."}</p>
              )}
            </motion.div>
          </div>

          {/* Right Column: Sticky Summary */}
          <div style={rightColumn}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={summaryStickyCard}>
              <h3 style={summaryTitle}>Price Details</h3>
              <div style={priceRow}><span>Original Price</span><span>{formatPrice(originalTotal)}</span></div>
              <div style={priceRow}><span style={{ color: "#10b981" }}>Discount</span><span style={{ color: "#10b981" }}>- {formatPrice(totalSavings)}</span></div>
              <div style={priceRow}><span>Delivery Charges</span><span style={{ color: "#10b981" }}>FREE</span></div>
              <div style={priceRow}><span>Platform Fee</span><span>{formatPrice(otherCharges)}</span></div>
              
              <div style={divider} />
              <div style={totalRow}><span>Total Amount</span><span>{formatPrice(finalTotal)}</span></div>
              
              <div style={savingsTag}>You will save {formatPrice(totalSavings)} on this order</div>

              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setIsCheckoutModalOpen(true)} style={checkoutBtn}>
                Proceed to Payment <FaCreditCard style={{marginLeft: '10px'}} />
              </motion.button>
            </motion.div>
          </div>

        </div>
      </main>

      {/* Payment Modal */}
      <AnimatePresence>
        {isCheckoutModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={modalOverlay}>
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }} style={modalContent}>
              <button onClick={() => setIsCheckoutModalOpen(false)} style={closeModalBtn}><FaTimes /></button>
              <h2 style={{ marginBottom: '20px' }}>Choose Payment Method</h2>
              
              <div style={paymentGrid}>
                {[
                  { id: 'UPI', icon: <FaGooglePay size={30} />, label: 'UPI / GPay' },
                  { id: 'Card', icon: <FaCcVisa size={30} />, label: 'Card' },
                  { id: 'COD', icon: <FaMoneyBillWave size={30} />, label: 'Cash' },
                ].map(m => (
                  <div key={m.id} onClick={() => setPaymentMethod(m.id)} style={{
                    ...paymentCard,
                    border: paymentMethod === m.id ? '2px solid #3b82f6' : '2px solid #f1f5f9',
                    background: paymentMethod === m.id ? '#eff6ff' : '#fff'
                  }}>
                    {m.icon}
                    <span>{m.label}</span>
                    {paymentMethod === m.id && <FaCheckCircle style={checkIcon} />}
                  </div>
                ))}
              </div>

              {paymentMethod === 'UPI' && <input style={modalInput} placeholder="Enter your UPI ID (e.g. user@okhdfc)" />}
              
              <button onClick={handleSubmitPayment} style={finalPayBtn}>Pay {formatPrice(finalTotal)}</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Fotter />
    </div>
  );
};

// --- Styles ---
const mainContentStyle = { maxWidth: "1200px", margin: "0 auto", padding: "100px 20px 40px" };
const contentGrid = { display: "grid", gridTemplateColumns: "window.innerWidth > 992 ? '1fr 380px' : '1fr'", gap: "30px", alignItems: "start" };

// Responsive adjustment logic usually goes in a CSS file or via a Hook, but for this component:
if (typeof window !== 'undefined' && window.innerWidth < 992) {
  contentGrid.gridTemplateColumns = "1fr";
}

const leftColumn = { display: "flex", flexDirection: "column", gap: "20px" };
const rightColumn = { position: "sticky", top: "100px" };

const sectionCard = { background: "#fff", padding: "24px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" };
const summaryStickyCard = { background: "#fff", padding: "24px", borderRadius: "16px", boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)", border: "1px solid #e2e8f0" };

const sectionTitle = { fontSize: "1.25rem", fontWeight: "700", color: "#1e293b", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" };
const itemRow = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderBottom: "1px solid #f1f5f9" };
const itemInfo = { display: "flex", alignItems: "center", gap: "15px" };
const itemBadge = { background: "#f1f5f9", padding: "4px 10px", borderRadius: "8px", fontWeight: "700", fontSize: "0.85rem" };
const itemName = { margin: 0, fontSize: "1rem", color: "#334155" };
const itemSubtext = { margin: 0, fontSize: "0.8rem", color: "#64748b" };

const itemPriceBlock = { textAlign: "right", display: "flex", flexDirection: "column" };
const oldPriceText = { fontSize: "0.8rem", color: "#94a3b8", textDecoration: "line-through" };
const newPriceText = { fontSize: "1rem", fontWeight: "700", color: "#0f172a" };

const flexHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" };
const editBtn = { background: "none", border: "none", color: "#3b82f6", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "5px" };
const addressDisplay = { color: "#475569", lineHeight: "1.6", fontSize: "0.95rem", paddingLeft: "32px" };
const addressInput = { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none" };

const summaryTitle = { fontSize: "1.1rem", fontWeight: "700", color: "#475569", marginBottom: "20px", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px" };
const priceRow = { display: "flex", justifyContent: "space-between", marginBottom: "12px", color: "#64748b", fontSize: "0.95rem" };
const totalRow = { display: "flex", justifyContent: "space-between", fontSize: "1.25rem", fontWeight: "800", color: "#1e293b", margin: "20px 0" };
const divider = { height: "1px", background: "#e2e8f0", margin: "15px 0" };
const savingsTag = { background: "#ecfdf5", color: "#059669", padding: "10px", borderRadius: "8px", fontSize: "0.85rem", fontWeight: "600", textAlign: "center", marginBottom: "20px" };
const checkoutBtn = { width: "100%", padding: "16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "700", cursor: "pointer", transition: "all 0.2s" };

const modalOverlay = { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(15, 23, 42, 0.7)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000 };
const modalContent = { background: "#fff", width: "90%", maxWidth: "500px", padding: "30px", borderRadius: "24px", position: "relative" };
const paymentGrid = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" };
const paymentCard = { display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "20px 10px", borderRadius: "16px", cursor: "pointer", position: "relative", transition: "0.2s" };
const checkIcon = { position: "absolute", top: "10px", right: "10px", color: "#3b82f6" };
const modalInput = { width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px" };
const finalPayBtn = { width: "100%", padding: "16px", background: "#1e293b", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "700", cursor: "pointer" };
const closeModalBtn = { position: "absolute", top: "20px", right: "20px", border: "none", background: "#f1f5f9", width: "32px", height: "32px", borderRadius: "50%", cursor: "pointer" };

export default OrderSummary;